import React, { useCallback, useEffect, useMemo, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import Button from "@mui/material/Button";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { API_get_autoschedule_role_profile } from "../../../../../constants";
import { getRightworkAPI } from "../../../requests/utilities/requests";
import PrebuiltReportShell from "../PrebuiltReportShell";
import { unwrapPayload } from "./_shared/laborMath";

const ROLE_COLORS = [
  "#0f766e",
  "#2563eb",
  "#7c3aed",
  "#dc2626",
  "#ca8a04",
  "#0891b2",
  "#be185d",
  "#4f46e5",
  "#16a34a",
  "#9333ea",
];

const TARGET_LINE_COLOR = "#334155";
const SOURCE_LABELS = {
  published_schedule: "Published",
  actual_used_schedule: "Worked",
  actual_schedule: "Draft",
};

function formatNumber(value, digits = 1) {
  if (value == null || Number.isNaN(Number(value))) return "-";
  return Number(value).toFixed(digits);
}

function formatPercent(value, digits = 0) {
  if (value == null || Number.isNaN(Number(value))) return "-";
  return `${Number(value).toFixed(digits)}%`;
}

function formatCompactQh(value) {
  if (value == null || Number.isNaN(Number(value))) return "";
  const totalMinutes = Math.round(Number(value) * 60);
  const normalizedMinutes = ((totalMinutes % 1440) + 1440) % 1440;
  const hour = Math.floor(normalizedMinutes / 60);
  const minutes = normalizedMinutes % 60;
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const suffix = hour >= 12 ? "p" : "a";
  return `${displayHour}${minutes ? `:${String(minutes).padStart(2, "0")}` : ""}${suffix}`;
}

function formatQh(value) {
  if (value == null || Number.isNaN(Number(value))) return "-";
  const totalMinutes = Math.round(Number(value) * 60);
  const hour = Math.floor(totalMinutes / 60);
  const minutes = Math.abs(totalMinutes % 60);
  const displayHour = hour % 24;
  const suffix = hour >= 24 ? ` +${Math.floor(hour / 24)}d` : "";
  return `${String(displayHour).padStart(2, "0")}:${String(minutes).padStart(2, "0")}${suffix}`;
}

function sourceText(sourceCounts = {}) {
  const entries = Object.entries(sourceCounts).filter(([, count]) => count > 0);
  if (!entries.length) return "-";
  return entries
    .map(([source, count]) => `${SOURCE_LABELS[source] || source}: ${count}`)
    .join(" / ");
}

function confidenceTone(value) {
  if (value == null) return "bg-slate-100 text-slate-500";
  if (value >= 75) return "bg-emerald-100 text-emerald-700";
  if (value >= 50) return "bg-amber-100 text-amber-700";
  return "bg-rose-100 text-rose-700";
}

function getRoleKey(row) {
  if (row.role_id != null) return `role_${row.role_id}`;
  return `role_${String(row.role_name || row.role_type || "unmapped").replace(/[^a-zA-Z0-9_]/g, "_")}`;
}

function getDateOptions(rows) {
  return Array.from(new Set((rows || []).map((row) => row.date).filter(Boolean))).sort();
}

function getRoleTypeOptions(rows, selectedDate) {
  return Array.from(
    new Set(
      (rows || [])
        .filter((row) => !selectedDate || row.date === selectedDate)
        .map((row) => row.role_type)
        .filter(Boolean)
    )
  ).sort();
}

function buildSplitView(rows, selectedDate, selectedRoleType) {
  const roleMetas = new Map();
  const grouped = new Map();

  (rows || [])
    .filter((row) => row.date === selectedDate)
    .filter((row) => row.role_type === selectedRoleType)
    .forEach((row) => {
      const qhNum = Number(row.qh_num);
      if (Number.isNaN(qhNum)) return;

      const roleKey = getRoleKey(row);
      if (!roleMetas.has(roleKey)) {
        roleMetas.set(roleKey, {
          key: roleKey,
          label: row.role_name || "Unmapped role",
          color: ROLE_COLORS[roleMetas.size % ROLE_COLORS.length],
        });
      }

      const optimalHeadcount = Number(row.optimal_headcount || 0);
      const targetHeadcount = Number(row.role_type_optimal_headcount || 0);
      const existing = grouped.get(qhNum) || {
        qh_num: qhNum,
        role_type_total: targetHeadcount,
        role_sum: 0,
        confidenceTotal: 0,
        confidenceWeight: 0,
        fallbackCount: 0,
        roles: [],
      };

      existing[roleKey] = Number(((existing[roleKey] || 0) + optimalHeadcount).toFixed(4));
      existing.role_type_total = Math.max(existing.role_type_total, targetHeadcount);
      existing.role_sum = Number((existing.role_sum + optimalHeadcount).toFixed(4));
      if (row.confidence != null && !Number.isNaN(Number(row.confidence))) {
        const confidenceWeight = optimalHeadcount || 1;
        existing.confidenceTotal += Number(row.confidence) * confidenceWeight;
        existing.confidenceWeight += confidenceWeight;
      }
      if (row.allocation_method === "role_type_fallback") {
        existing.fallbackCount += 1;
      }
      existing.roles.push({
        key: roleKey,
        roleName: row.role_name || "Unmapped role",
        headcount: optimalHeadcount,
        percent: targetHeadcount ? (optimalHeadcount / targetHeadcount) * 100 : null,
        confidence: row.confidence,
        sampleDays: row.sample_days,
        activeDays: row.active_days,
        allocationMethod: row.allocation_method,
      });
      grouped.set(qhNum, existing);
    });

  const data = Array.from(grouped.values())
    .sort((a, b) => a.qh_num - b.qh_num)
    .map((row) => ({
      ...row,
      avgConfidence: row.confidenceWeight ? row.confidenceTotal / row.confidenceWeight : null,
      roles: row.roles.sort((a, b) => b.headcount - a.headcount || a.roleName.localeCompare(b.roleName)),
    }));

  return {
    data,
    roles: Array.from(roleMetas.values()),
  };
}

function buildHourTicks(data) {
  if (!data.length) return [];
  const minHour = Math.floor(data[0].qh_num);
  const maxHour = Math.ceil(data[data.length - 1].qh_num);
  const ticks = [];
  for (let hour = minHour; hour <= maxHour; hour += 2) {
    ticks.push(hour);
  }
  if (!ticks.includes(maxHour)) ticks.push(maxHour);
  return ticks;
}

function buildSummary(chartData, roles, profile) {
  if (!chartData.length) {
    return {
      peakTarget: null,
      peakTime: null,
      avgTarget: null,
      avgConfidence: null,
      fallbackRows: 0,
      roleCount: roles.length,
      historyWeeks: profile?.baseline_weeks,
    };
  }

  const peakRow = chartData.reduce(
    (best, row) => (row.role_type_total > best.role_type_total ? row : best),
    chartData[0]
  );
  const confidenceRows = chartData.filter((row) => row.avgConfidence != null);
  return {
    peakTarget: peakRow.role_type_total,
    peakTime: peakRow.qh_num,
    avgTarget: chartData.reduce((total, row) => total + row.role_type_total, 0) / chartData.length,
    avgConfidence: confidenceRows.length
      ? confidenceRows.reduce((total, row) => total + row.avgConfidence, 0) / confidenceRows.length
      : null,
    fallbackRows: chartData.reduce((total, row) => total + row.fallbackCount, 0),
    roleCount: roles.length,
    historyWeeks: profile?.baseline_weeks,
  };
}

function SplitTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-sm">
      <div className="font-semibold text-slate-900">{formatQh(row.qh_num)}</div>
      <div className="mt-1 text-slate-600">
        Role type target: {formatNumber(row.role_type_total, 2)}
      </div>
      <div className="text-slate-600">Allocated: {formatNumber(row.role_sum, 2)}</div>
      <div className="mt-2 space-y-0.5">
        {row.roles.map((role) => (
          <div key={role.key} className="flex items-center justify-between gap-4">
            <span>{role.roleName}</span>
            <span className="font-medium text-slate-700">
              {formatNumber(role.headcount, 2)} ({formatPercent(role.percent)})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, detail }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 text-lg font-semibold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500">{detail}</div>
    </div>
  );
}

function RoleTargetSplitChart({ chart, selectedRoleType }) {
  const hourTicks = useMemo(() => buildHourTicks(chart.data), [chart.data]);

  if (!chart.data.length) {
    return (
      <div className="border border-slate-200 rounded-lg bg-white px-4 py-8 text-sm text-slate-500">
        No role target split is available for this date and role type.
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-lg bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Recommended Labor Split</h3>
          <div className="text-xs text-slate-500">
            {selectedRoleType} target split across specific role targets through the day.
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          {chart.roles.map((role) => (
            <div key={role.key} className="flex items-center gap-2 text-xs text-slate-600">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: role.color }} />
              <span>{role.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="h-0.5 w-4" style={{ backgroundColor: TARGET_LINE_COLOR }} />
            <span>Total target</span>
          </div>
        </div>
      </div>

      <div className="mt-4 h-[390px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chart.data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
            <XAxis
              dataKey="qh_num"
              type="number"
              domain={["dataMin", "dataMax"]}
              ticks={hourTicks}
              tickFormatter={formatCompactQh}
              tick={{ fontSize: 11, fill: "#64748b" }}
              stroke="#cbd5e1"
            />
            <YAxis
              domain={[0, (dataMax) => Math.max(1, Math.ceil(dataMax + 0.5))]}
              tick={{ fontSize: 11, fill: "#64748b" }}
              stroke="#cbd5e1"
            />
            <Tooltip content={<SplitTooltip />} />
            {chart.roles.map((role) => (
              <Area
                key={role.key}
                type="stepAfter"
                dataKey={role.key}
                name={role.label}
                stackId="roleTarget"
                fill={role.color}
                fillOpacity={0.74}
                stroke={role.color}
                strokeWidth={1}
                isAnimationActive={false}
              />
            ))}
            <Line
              type="stepAfter"
              dataKey="role_type_total"
              name="Total target"
              stroke={TARGET_LINE_COLOR}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function SplitDetailTable({ rows }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-900">Quarter-Hour Split</h3>
        <div className="text-xs text-slate-500">
          Each row shows how the role type target is allocated to role targets.
        </div>
      </div>
      <div className="overflow-x-auto max-h-[460px]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 sticky top-0">
            <tr>
              <th className="text-left font-semibold px-4 py-2">Time</th>
              <th className="text-right font-semibold px-4 py-2">Total Target</th>
              <th className="text-left font-semibold px-4 py-2">Role Target Split</th>
              <th className="text-right font-semibold px-4 py-2">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.qh_num} className="border-t border-slate-100 align-top">
                <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                  {formatQh(row.qh_num)}
                </td>
                <td className="px-4 py-3 text-right font-medium text-slate-900">
                  {formatNumber(row.role_type_total, 2)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-2">
                    {row.roles.map((role) => (
                      <span
                        key={role.key}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700"
                      >
                        <span className="font-medium text-slate-900">{role.roleName}</span>
                        <span>{formatNumber(role.headcount, 2)}</span>
                        <span className="text-slate-500">{formatPercent(role.percent)}</span>
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${confidenceTone(row.avgConfidence)}`}>
                    {formatPercent(row.avgConfidence)}
                  </span>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                  No quarter-hour split rows for this selection.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EvidenceSummary({ profile }) {
  const summary = profile?.summary || {};
  const sourceSummary = profile?.source_summary || {};
  const selectedShiftCounts = sourceSummary.selected_shift_counts || {};

  return (
    <div className="border border-slate-200 rounded-lg bg-white px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-400">Basis</div>
      <div className="mt-1 text-sm text-slate-700">
        {profile?.baseline_weeks || "-"} weeks of selected history, {summary.selected_shift_count || 0} selected shifts.
      </div>
      <div className="mt-1 text-xs text-slate-500">
        Sources: {sourceText(selectedShiftCounts)}
      </div>
    </div>
  );
}

export default function AutoscheduleRoleProfileReport({
  report,
  homeStore,
  onBack,
  storePicker,
}) {
  const [baselineWeeks, setBaselineWeeks] = useState(6);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedRoleType, setSelectedRoleType] = useState("");
  const [payload, setPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchProfile = useCallback(async () => {
    if (!homeStore) return;
    setIsLoading(true);
    setError("");
    try {
      const response = await getRightworkAPI(API_get_autoschedule_role_profile, {
        store_id: homeStore,
        baseline_weeks: baselineWeeks,
      });
      setPayload(unwrapPayload(response));
    } catch (err) {
      console.error("Error loading autoschedule role targets", err);
      setError("Unable to load role targets for this store.");
    } finally {
      setIsLoading(false);
    }
  }, [homeStore, baselineWeeks]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const profile = payload?.profile;
  const optimalRows = useMemo(() => payload?.optimal_rows || [], [payload]);
  const dateOptions = useMemo(() => getDateOptions(optimalRows), [optimalRows]);
  const roleTypeOptions = useMemo(
    () => getRoleTypeOptions(optimalRows, selectedDate),
    [optimalRows, selectedDate]
  );

  useEffect(() => {
    if (!dateOptions.length) {
      if (selectedDate) setSelectedDate("");
      return;
    }
    if (!dateOptions.includes(selectedDate)) {
      setSelectedDate(dateOptions[0]);
    }
  }, [dateOptions, selectedDate]);

  useEffect(() => {
    if (!roleTypeOptions.length) {
      if (selectedRoleType) setSelectedRoleType("");
      return;
    }
    if (!roleTypeOptions.includes(selectedRoleType)) {
      setSelectedRoleType(roleTypeOptions[0]);
    }
  }, [roleTypeOptions, selectedRoleType]);

  const chart = useMemo(
    () => buildSplitView(optimalRows, selectedDate, selectedRoleType),
    [optimalRows, selectedDate, selectedRoleType]
  );
  const summary = useMemo(
    () => buildSummary(chart.data, chart.roles, profile),
    [chart.data, chart.roles, profile]
  );

  const filters = (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
        History
        <select
          className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
          value={baselineWeeks}
          onChange={(event) => setBaselineWeeks(Number(event.target.value))}
        >
          {[4, 6, 8, 10, 12].map((weeks) => (
            <option key={weeks} value={weeks}>{weeks} weeks</option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
        Date
        <select
          className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
          value={selectedDate}
          onChange={(event) => setSelectedDate(event.target.value)}
          disabled={!dateOptions.length}
        >
          {dateOptions.map((dateOption) => (
            <option key={dateOption} value={dateOption}>{dateOption}</option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
        Role Type
        <select
          className="h-9 min-w-[180px] rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
          value={selectedRoleType}
          onChange={(event) => setSelectedRoleType(event.target.value)}
          disabled={!roleTypeOptions.length}
        >
          {roleTypeOptions.map((roleType) => (
            <option key={roleType} value={roleType}>{roleType}</option>
          ))}
        </select>
      </label>
    </div>
  );

  const actions = (
    <Button
      variant="outlined"
      size="small"
      startIcon={<RefreshIcon />}
      onClick={fetchProfile}
      disabled={isLoading}
      sx={{
        borderColor: "#0f766e",
        color: "#0f766e",
        textTransform: "none",
        "&:hover": { borderColor: "#115e59", backgroundColor: "#f0fdfa" },
      }}
    >
      Refresh
    </Button>
  );

  return (
    <PrebuiltReportShell
      report={report}
      onBack={onBack}
      filters={filters}
      storePicker={storePicker}
      actions={actions}
      isLoading={isLoading}
    >
      {error ? (
        <div className="border border-rose-200 bg-rose-50 text-rose-700 rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            <SummaryCard
              label="Peak Target"
              value={formatNumber(summary.peakTarget, 2)}
              detail={formatQh(summary.peakTime)}
            />
            <SummaryCard
              label="Average Target"
              value={formatNumber(summary.avgTarget, 2)}
              detail={`${chart.data.length} quarter-hours`}
            />
            <SummaryCard
              label="Role Targets"
              value={summary.roleCount || 0}
              detail={selectedRoleType || "selected role type"}
            />
            <SummaryCard
              label="Confidence"
              value={formatPercent(summary.avgConfidence)}
              detail={`${summary.historyWeeks || "-"} week basis`}
            />
            <SummaryCard
              label="Fallback Rows"
              value={summary.fallbackRows || 0}
              detail="where history could not split roles"
            />
          </div>

          <RoleTargetSplitChart chart={chart} selectedRoleType={selectedRoleType} />
          <SplitDetailTable rows={chart.data} />
          <EvidenceSummary profile={profile} />
        </div>
      )}
    </PrebuiltReportShell>
  );
}
